import { useState, useEffect } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { Wifi, WifiOff, RefreshCw, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { onOfflineChange, syncQueue, getPendingCount } from "../offline/interceptor";
import toast from "react-hot-toast";

/**
 * Header indicator for online/offline + last sync info.
 * - Listens to navigator online/offline events
 * - Shows last sync timestamp + a small popover with details
 */
export default function SyncStatusIndicator({ compact = false }) {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [lastSync, setLastSync] = useState(() => {
    try {
      return localStorage.getItem("last_sync_at") || null;
    } catch {
      return null;
    }
  });
  const [pingMs, setPingMs] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  // Listen for offline queue updates
  useEffect(() => {
    getPendingCount().then(setPendingCount).catch(() => {});
    const unsub = onOfflineChange(({ pendingCount }) => {
      setPendingCount(pendingCount);
    });
    return unsub;
  }, []);

  const handleManualSync = async () => {
    if (syncing || !navigator.onLine) {
      if (!navigator.onLine) toast.error('لا يوجد اتصال بالإنترنت');
      return;
    }
    setSyncing(true);
    try {
      const res = await syncQueue();
      if (res.synced > 0) {
        toast.success(`تمت مزامنة ${res.synced} عملية`);
      }
      if (res.failed > 0) {
        toast.error(`فشلت ${res.failed} عملية — ستُعاد المحاولة تلقائياً`);
      }
      if (res.synced === 0 && res.failed === 0) {
        toast('لا توجد عمليات للمزامنة', { icon: '✓' });
      }
      const c = await getPendingCount();
      setPendingCount(c);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // Periodic ping to backend (proves real connectivity, not just network card up)
    let cancelled = false;
    const ping = async () => {
      if (!navigator.onLine) {
        setPingMs(null);
        return;
      }
      const t0 = performance.now();
      try {
        const res = await fetch(`${window.location.origin}/api/`, {
          method: "GET",
          cache: "no-store",
        });
        if (cancelled) return;
        if (res.ok) {
          const ms = Math.round(performance.now() - t0);
          setPingMs(ms);
          const ts = new Date().toISOString();
          setLastSync(ts);
          try { localStorage.setItem("last_sync_at", ts); } catch {}
          setOnline(true);
        } else {
          setPingMs(null);
        }
      } catch {
        if (!cancelled) {
          setPingMs(null);
          setOnline(false);
        }
      }
    };
    ping();
    const id = setInterval(ping, 30000); // every 30s
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const formatLastSync = (iso) => {
    if (!iso) return "لم يحدث بعد";
    try {
      const d = new Date(iso);
      const diff = Math.floor((Date.now() - d.getTime()) / 1000);
      if (diff < 5) return "الآن";
      if (diff < 60) return `قبل ${diff} ثانية`;
      if (diff < 3600) return `قبل ${Math.floor(diff / 60)} دقيقة`;
      if (diff < 86400) return `قبل ${Math.floor(diff / 3600)} ساعة`;
      return d.toLocaleString("ar-IQ");
    } catch {
      return iso;
    }
  };

  // Status logic
  let statusColor = "text-emerald-400";
  let statusBg = "bg-emerald-500/15 border-emerald-500/30";
  let StatusIcon = Wifi;
  let label = "متصل";

  if (!online) {
    statusColor = "text-rose-400";
    statusBg = "bg-rose-500/15 border-rose-500/30";
    StatusIcon = WifiOff;
    label = "غير متصل";
  } else if (online && pingMs === null && lastSync) {
    statusColor = "text-amber-400";
    statusBg = "bg-amber-500/15 border-amber-500/30";
    StatusIcon = AlertCircle;
    label = "اتصال ضعيف";
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? "icon" : "sm"}
          data-testid="sync-status-indicator-btn"
          className={`relative border ${statusBg} hover:opacity-90 ${
            compact ? "h-9 w-9" : "h-9 px-2 gap-1.5"
          }`}
        >
          <span className={`relative flex items-center ${statusColor}`}>
            <StatusIcon className="w-4 h-4" />
            {online && pendingCount === 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            )}
            {pendingCount > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 bg-amber-500 text-slate-900 text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center"
                data-testid="pending-sync-badge"
              >
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </span>
          {!compact && (
            <span className={`text-xs font-medium ${statusColor}`}>{label}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 bg-slate-900 border-slate-700 text-slate-100 p-4"
        data-testid="sync-status-popover"
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold">حالة الاتصال</span>
            <span
              className={`text-xs px-2 py-0.5 rounded-full border ${statusBg} ${statusColor}`}
              data-testid="sync-status-label"
            >
              {label}
            </span>
          </div>

          <div className="border-t border-slate-700 pt-3 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-slate-400 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                آخر مزامنة
              </span>
              <span className="text-slate-200" data-testid="last-sync-time">
                {formatLastSync(lastSync)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-slate-400 flex items-center gap-1.5">
                <RefreshCw className="w-3.5 h-3.5" />
                زمن الاستجابة
              </span>
              <span className="text-slate-200">
                {pingMs !== null ? `${pingMs} ms` : "—"}
              </span>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-3 text-xs text-slate-400 leading-relaxed">
            {online ? (
              <span>التطبيق يعمل أونلاين. كل البيانات تُحفظ مباشرة على الخادم.</span>
            ) : (
              <span className="text-amber-300">
                لا يوجد اتصال بالإنترنت. التطبيق يعمل من الكاش المحلي. العمليات الجديدة ستُحفظ وتُرسل عند عودة الاتصال.
              </span>
            )}
          </div>

          {pendingCount > 0 && (
            <div className="border-t border-slate-700 pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-amber-300 flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  عمليات في انتظار المزامنة
                </span>
                <span className="bg-amber-500/20 text-amber-300 text-xs font-bold px-2 py-0.5 rounded-full">
                  {pendingCount}
                </span>
              </div>
              <Button
                size="sm"
                onClick={handleManualSync}
                disabled={syncing || !online}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="manual-sync-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 me-1.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'جاري المزامنة...' : online ? 'مزامنة الآن' : 'في انتظار الاتصال'}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
